import React, { useCallback, useEffect } from 'react'

import Intercom from '@intercom/messenger-js-sdk'
import Link from 'next/link'
import {
  HiOutlineChatBubbleLeftRight,
  HiOutlineEnvelope,
} from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import { useAuthInfo } from '@sweetauth/core'

import Button from '@/components/common/Button'

import { INTERCOM_APP_ID } from '@/constants/env'
import { SUPPORT_EMAIL } from '@/constants/general'

import { useBillingOrganization } from '@/hooks/billing/useBillingOrganization'
import { useIntercomToken } from '@/hooks/intercom/userIntercomToken'

import { readSupportHandoffRequest } from '@/support/intercom/supportHandoff'
import type { IntercomButtonProps } from '@/support/intercom/types'

type IntercomWindow = typeof window & {
  Intercom?: (command: string, payload?: string) => void
}

const getIntercom = () =>
  typeof window === 'undefined'
    ? undefined
    : (window as IntercomWindow).Intercom

const openSupportHandoff = () => {
  if (typeof window === 'undefined') return

  const request = readSupportHandoffRequest(window.location.href)
  if (request === null) return

  // Stripping the handoff params synchronously is what makes this idempotent:
  // every other mounted button (and StrictMode re-runs) reads the cleaned URL
  // and bails on the null request above.
  window.history.replaceState(window.history.state, '', request.cleanPath)
  window.setTimeout(() => {
    const intercom = getIntercom()
    if (request.message === null) {
      intercom?.('show')
    } else {
      intercom?.('showNewMessage', request.message)
    }
  }, 50)
}

const composeEventHandlers =
  <E extends React.SyntheticEvent>(
    originalHandler?: (event: E) => void,
    nextHandler?: (event: E) => void,
  ) =>
  (event: E) => {
    originalHandler?.(event)
    if (event.defaultPrevented) return
    nextHandler?.(event)
  }

export const IntercomButton: React.FC<IntercomButtonProps> = ({
  children,
  asChild,
  ...props
}) => {
  const { data: token, isLoading: isTokenLoading } = useIntercomToken()
  const { user, isLoggedIn } = useAuthInfo()
  const { data: billingOrg, isLoading: isBillingOrgLoading } =
    useBillingOrganization()
  const isLoading = isTokenLoading || isBillingOrgLoading
  const { className, disabled, variant, onClick, presetMessage, ...rest } =
    props

  const openIntercom = useCallback(() => {
    const intercom = getIntercom()
    if (!intercom) return false
    if (presetMessage) {
      window.setTimeout(() => {
        intercom('showNewMessage', presetMessage)
      }, 50)
    }
    return true
  }, [presetMessage])

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(event)
      if (event.defaultPrevented || disabled || isLoading) return
      openIntercom()
    },
    [disabled, isLoading, onClick, openIntercom],
  )

  useEffect(() => {
    if (token && user) {
      Intercom({
        app_id: INTERCOM_APP_ID,
        avatar: user.pictureUrl,
        user_id: user.userId,
        user_hash: token,
        name: `${user.firstName} ${user.lastName}`,
        email: user.email,
        custom_launcher_selector: '.intercom-launcher',
      })
      openSupportHandoff()
    }
  }, [token])

  if (!isLoggedIn || billingOrg === undefined || billingOrg === null) {
    return null
  }

  if (billingOrg.tier === 'basic') {
    return (
      <Link href={`mailto:${SUPPORT_EMAIL}`}>
        <Button as="a" variant="outline" disabled={isLoading}>
          <HiOutlineEnvelope className="w-4 h-4" />
          <span className="text-sm font-semibold">Support</span>
        </Button>
      </Link>
    )
  }

  if (asChild) {
    const child = children as React.ReactElement
    return React.cloneElement(child, {
      className: twMerge('intercom-launcher', child.props.className, className),
      disabled: child.props.disabled ?? disabled ?? isLoading,
      onClick: composeEventHandlers(child.props.onClick, handleClick),
      ...rest,
    })
  }

  return (
    <Button
      className={twMerge('intercom-launcher', className)}
      disabled={disabled || isLoading}
      variant={variant ?? 'primary-compact'}
      onClick={handleClick}
      {...rest}
    >
      {children ?? (
        <>
          <HiOutlineChatBubbleLeftRight className="flex-shrink-0 w-4 h-4" />
          <span className="text-sm font-medium">Live Support</span>
        </>
      )}
    </Button>
  )
}
